import * as THREE from "three";

// Compute an orthographic zoom that frames the whole (now tighter) campus given
// the canvas size. Denominators reduced vs the old road-grid world so the smaller
// nature campus fills the frame at the SAME isometric angle.
export function fitZoom(size) {
  if (!size || !size.width || !size.height) return 13;
  const z = Math.min(size.width / 78, size.height / 56);
  return THREE.MathUtils.clamp(z, 5, 70);
}

export function focusZoom(size) {
  return fitZoom(size) * 2.9;
}

export function cityCenter(buildings) {
  if (!buildings || !buildings.length) return new THREE.Vector3(0, 2, 0);
  let x = 0;
  let z = 0;
  buildings.forEach((b) => {
    x += Number(b.positionX) || 0;
    z += Number(b.positionZ) || 0;
  });
  return new THREE.Vector3(x / buildings.length, 2, z / buildings.length);
}

export function findBuilding(buildings, key) {
  if (!key) return null;
  return buildings.find((b) => b.slug === key || b.id === key) || null;
}

// ---- Organic world helpers (grass campus layout, no roads) ----

// Deterministic seeded RNG so scattered placement is stable across renders.
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Multiple DETAILED water bodies spread around the campus (rendered with shoreline
// + reeds + lily pads in Ground.js). Citizens + props treat them as obstacles.
export const LAKES = [
  { x: -24, z: 22, r: 5.2 },   // front-left feature lake
  { x: 25, z: -20, r: 4.8 },   // back-right corner lake
  { x: 30, z: 9, r: 3.2 },     // right-edge pond
  { x: -8, z: 31, r: 3.0 },    // front pond near the drill down
];

// Roads + parking are removed in the nature-campus pivot. These remain as EMPTY
// exports so any legacy helper that maps over them is simply a no-op.
export const ROADS = [];
export const LOTS = [];

// How far in front of a door a citizen stages before walking in.
export const APRON = 2.8;

// Rotation-aware door geometry shared by Life.js (citizens + doors) and Paths.js
// (stone walkways). A building's front is local +Z (drill-down faces -Z); applying
// the building's `rotationY` turns that normal into world space so the door — and
// the path that meets it — always sits on the face that points toward the campus.
// The offset is the building's LOCAL front half-depth (the door lives on that face),
// so it stays flush no matter how the building is rotated.
export function doorGeom(b) {
  const w = Number(b.width) || 4;
  const d = Number(b.depth) || 4;
  const rot = Number(b.rotationY) || 0;
  const bnz = b.slug === "drilldown" ? -1 : 1;
  // local front normal (+Z, or -Z for drill-down) rotated into world by `rot`
  const nx = bnz * Math.sin(rot);
  const nz = bnz * Math.cos(rot);
  // distance from the building centre to its FRONT face (independent of rotation)
  let off = d / 2 + 0.1;
  if (b.shape === "beacon") off = w * 0.58 + 0.12;
  else if (b.slug === "experiential") off = w * 0.74 + 0.16;
  else if (b.slug === "consulting") off = d * 0.575 + 0.16;
  else if (b.slug === "marketing") off = (d + 0.9) / 2 + 0.12;
  const bx = Number(b.positionX) || 0;
  const bz = Number(b.positionZ) || 0;
  const dx = bx + nx * off;
  const dz = bz + nz * off;
  return {
    bx, bz, nx, nz, off, dx, dz,
    rotY: Math.atan2(nx, nz),
    apronX: dx + nx * APRON,
    apronZ: dz + nz * APRON,
  };
}

// Scatter `count` points across a disc, avoiding obstacles + keeping min spacing.
// Returns [{ x, z, a, rnd, i }] — deterministic for a given seed.
export function scatterPositions({
  count,
  radius,
  innerHole = 0,
  seed = 1,
  obstacles = [],
  avoidDist = 5,
  minSpacing = 2.4,
  maxTries = 50,
}) {
  const rng = mulberry32(seed);
  const pts = [];
  let guard = 0;
  const limit = count * maxTries;
  while (pts.length < count && guard < limit) {
    guard++;
    const r = innerHole + Math.sqrt(rng()) * (radius - innerHole);
    const a = rng() * Math.PI * 2;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const nearObstacle = obstacles.some(
      (o) => Math.hypot((Number(o.x) || 0) - x, (Number(o.z) || 0) - z) < (o.r != null ? o.r + 1.5 : avoidDist)
    );
    if (nearObstacle) continue;
    if (pts.some((p) => Math.hypot(p.x - x, p.z - z) < minSpacing)) continue;
    pts.push({ x, z, a, rnd: rng(), i: pts.length });
  }
  return pts;
}

// Shared bench placement so Decor (renders them) and Life (people sit on them)
// agree. Benches are spread across the WHOLE campus (not just the centre) so
// citizens have somewhere to rest everywhere.
export function getBenches(buildings) {
  const obs = worldObstacles(buildings, 5);
  obs.push({ x: 9, z: 9, r: 3.5 }); // central fountain (see Decor.js)
  return scatterPositions({ count: 16, radius: 31, innerHole: 6, seed: 67, obstacles: obs, avoidDist: 4.5, minSpacing: 5 });
}

// Building radius scales with its footprint so big buildings never get trees/props
// spawned overlapping them.
export function worldObstacles(buildings, extraDist = 5) {
  const obs = (buildings || []).map((b) => {
    const w = Number(b.width) || 4;
    const d = Number(b.depth) || 4;
    const r = Math.max(extraDist, Math.max(w, d) * 0.5 + 2.6);
    return { x: Number(b.positionX) || 0, z: Number(b.positionZ) || 0, r };
  });
  LAKES.forEach((l) => obs.push({ x: l.x, z: l.z, r: l.r + 1.0 }));
  // Owl Shrine corner footprint so flora/props never spawn inside it.
  obs.push({ x: -29, z: -29, r: 4.2 });
  return obs;
}
